import React from "react";
import { FormattedMessage } from "react-intl";

import hash from "object-hash";

import { formatDate } from "@utils/utils";

const CRIME_CASE_TYPES = {
  CRIME: ["B", "SF"],
  INDICTMENT: ["A"],
};

const processRawDataItem = (rawDataItem, additionalDetail) => {
  const itemToSave = {
    ...rawDataItem,
    crime_court_type:
      rawDataItem?.crime_court_type ?? additionalDetail?.crime_court_type,
    crime_penalty:
      rawDataItem?.crime_penalty ?? additionalDetail?.crime_penalty,
  };

  if (itemToSave.isDeleted) {
    delete itemToSave.isDeleted;
  }

  if (itemToSave.isAdded) {
    delete itemToSave.isAdded;
  }

  if (itemToSave.isChanged) {
    delete itemToSave.isChanged;
  }

  itemToSave.hash = hash(itemToSave);

  return itemToSave;
};

export const prettifyProcessedData = rawData => {
  return rawData.processed_data?.map(item => {
    const additionalDetail =
      rawData?.analysed_data?.find(
        processedItem =>
          processedItem?.crime_case_number === item?.crime_case_number
      ) ?? {};

    return processRawDataItem(item, additionalDetail);
  });
};

export const getParsedCrimeData = rawData => {
  const resultMap = new Map([
    ["listOfCrimes", []],
    ["indictments", []],
    ["otherCases", []],
  ]);

  rawData.processed_data?.forEach(item => {
    const additionalDetail =
      rawData?.analysed_data?.find(
        processedItem =>
          processedItem?.crime_case_number === item?.crime_case_number
      ) ?? {};
    const itemToSave = processRawDataItem(item, additionalDetail);

    if (CRIME_CASE_TYPES.CRIME.includes(itemToSave.crime_case_type)) {
      resultMap.set("listOfCrimes", [
        ...resultMap.get("listOfCrimes"),
        itemToSave,
      ]);
    } else if (
      CRIME_CASE_TYPES.INDICTMENT.includes(itemToSave.crime_case_type)
    ) {
      resultMap.set("indictments", [
        ...resultMap.get("indictments"),
        itemToSave,
      ]);
    } else {
      resultMap.set("otherCases", [...resultMap.get("otherCases"), itemToSave]);
    }
  });

  return {
    listOfCrimes: resultMap.get("listOfCrimes"),
    dismissedCrimes: rawData.crimes_dismissed ?? [],
    indictments: resultMap.get("indictments"),
    otherCases: resultMap.get("otherCases"),
  };
};

export const getCrimesDataTableConfig = (
  data,
  isEditable,
  {
    onViewPenalty,
    onViewDescription,
    onViewExplanation,
    onOpenDeleteCrimeDialog,
    onOpenEditCrimeDialog,
  }
) => ({
  static_data: data,
  backend_querying: false,
  header: {},
  columns: [
    {
      db_field: "crime_name",
      title: "title_crime",
      type: "text",
      sortable: true,
      width: "15%",
    },
    {
      db_field: "crime_case_number",
      title: "title_case_number",
      type: "text",
      sortable: true,
      width: "13%",
    },
    {
      db_field: "crime_verdict_date",
      title: "label_crime_verdict_date",
      type: "date",
      sortable: true,
      width: "12%",
    },
    {
      db_field: "crime_case_type",
      title: "type_label",
      type: "text",
      width: "5%",
    },
    {
      db_field: "crime_court_type",
      title: "label_court_type",
      type: "text",
      sortable: true,
      width: "15%",
      formatter: courtType =>
        courtType ? <FormattedMessage id={`court_type_${courtType}`} /> : "-",
    },
    {
      db_field: "crime_court",
      title: "label_court_name",
      type: "text",
      sortable: true,
      width: "15%",
      formatter: (crimeCourt, { crime_court_name }) =>
        crimeCourt ?? crime_court_name,
    },
    {
      db_field: "crime_points",
      title: "label_points",
      type: "text",
      sortable: true,
      width: "5%",
    },
    {
      db_field: "crime_time_relevance",
      title: "label_time",
      type: "text",
      width: "8%",
    },
    {
      db_field: "crime_is_relevant",
      title: "label_relevance",
      type: "text",
      sortable: true,
      width: "7%",
      formatter: isRelevant =>
        isRelevant ? (
          <FormattedMessage id="yes_label" />
        ) : (
          <FormattedMessage id="no_label" />
        ),
    },
    {
      type: "actions",
      width: "5%",
      actions: [
        {
          label: <FormattedMessage id="label_download_court_document" />,
          icon: "icon-menu-report",
          type: "button",
          onClick: ({ document_download_link }) => {
            window.open(document_download_link, "_blank");
          },
        },
        {
          label: <FormattedMessage id="label_view_ai_explanation" />,
          icon: "icon-magic-wand",
          type: "button",
          onClick: ({ explanation }) => {
            onViewExplanation(explanation);
          },
        },
        {
          formatter: ({ crime_case_type }) =>
            crime_case_type === CRIME_CASE_TYPES.CRIME[0] ? (
              <FormattedMessage id="label_view_penalty" />
            ) : (
              <FormattedMessage id="label_view_description" />
            ),
          icon: "icon-file-download",
          type: "button",
          shouldHideAction: ({ crime_case_type }) =>
            !CRIME_CASE_TYPES.CRIME.includes(crime_case_type),
          onClick: ({ crime_case_type, crime_penalty, crime_description }) => {
            if (crime_case_type === CRIME_CASE_TYPES.CRIME[0]) {
              onViewPenalty(crime_penalty);
            } else {
              onViewDescription(crime_description);
            }
          },
        },
        {
          label: <FormattedMessage id="label_edit" />,
          icon: "icon-pen",
          type: "button",
          shouldHideAction: () => !isEditable,
          onClick: onOpenEditCrimeDialog,
        },
        {
          label: <FormattedMessage id="label_delete" />,
          icon: "icon-menu-trash",
          type: "button",
          shouldHideAction: () => !isEditable,
          onClick: onOpenDeleteCrimeDialog,
        },
      ],
    },
  ],
});

export const getDismissedCrimesDataTableConfig = (
  data,
  { onViewExplanation }
) => ({
  static_data: data,
  backend_querying: false,
  header: {},
  columns: [
    {
      db_field: "crime_name",
      title: "title_crime",
      type: "text",
      sortable: true,
      width: "20%",
    },
    {
      db_field: "crime_case_number",
      title: "title_case_number",
      type: "text",
      sortable: true,
      width: "20%",
    },
    {
      db_field: "crime_verdict_date",
      title: "label_crime_verdict_date",
      type: "date",
      width: "15%",
    },
    {
      db_field: "crime_case_type",
      title: "type_label",
      type: "text",
      width: "5%",
    },
    {
      db_field: "crime_court_type",
      title: "label_court_type",
      type: "text",
      sortable: true,
      width: "15%",
      formatter: courtType =>
        courtType ? <FormattedMessage id={`court_type_${courtType}`} /> : "-",
    },
    {
      db_field: "crime_court",
      title: "label_court_name",
      type: "text",
      sortable: true,
      width: "20%",
    },
    {
      type: "actions",
      width: "5%",
      actions: [
        {
          label: <FormattedMessage id="label_download_court_document" />,
          icon: "icon-menu-report",
          type: "button",
          onClick: ({ document_download_link }) => {
            window.open(document_download_link, "_blank");
          },
        },
        {
          label: <FormattedMessage id="label_view_ai_explanation" />,
          icon: "icon-magic-wand",
          type: "button",
          onClick: ({ explanation }) => {
            onViewExplanation(explanation);
          },
        },
      ],
    },
  ],
});

export const getIndictmentsDataTableConfig = (
  data,
  isEditable,
  { onViewExplanation, onOpenDeleteCrimeDialog, onOpenEditCrimeDialog }
) => ({
  static_data: data,
  backend_querying: false,
  header: {},
  columns: [
    {
      db_field: "crime_name",
      title: "title_crime",
      type: "text",
      sortable: true,
      width: "24%",
    },
    {
      db_field: "crime_case_number",
      title: "title_case_number",
      type: "text",
      sortable: true,
      width: "23%",
    },
    {
      db_field: "crime_court",
      title: "label_prosecution_office_name",
      type: "text",
      width: "24%",
    },
    {
      db_field: "crime_prosecution_status",
      title: "label_prosecution_status",
      type: "text",
      sortable: true,
      width: "24%",
      formatter: prosecutionStatus =>
        prosecutionStatus ? (
          <FormattedMessage id={`prosecution_status_${prosecutionStatus}`} />
        ) : (
          "-"
        ),
    },
    {
      type: "actions",
      width: "5%",
      actions: [
        {
          label: <FormattedMessage id="label_download_court_document" />,
          icon: "icon-download-assignment",
          type: "button",
          onClick: ({ document_download_link }) => {
            window.open(document_download_link, "_blank");
          },
        },
        {
          label: <FormattedMessage id="label_view_ai_explanation" />,
          icon: "icon-magic-wand",
          type: "button",
          onClick: ({ explanation }) => {
            onViewExplanation(explanation);
          },
        },
        {
          label: <FormattedMessage id="label_edit" />,
          icon: "icon-pen",
          type: "button",
          shouldHideAction: () => !isEditable,
          onClick: onOpenEditCrimeDialog,
        },
        {
          label: <FormattedMessage id="label_delete" />,
          icon: "icon-menu-trash",
          type: "button",
          shouldHideAction: () => !isEditable,
          onClick: onOpenDeleteCrimeDialog,
        },
      ],
    },
  ],
});

export const prepareCrimeData = ({
  court,
  crime,
  crime_verdict_date,
  ...restCrimeData
}) => ({
  ...restCrimeData,
  crime_name: crime.name ?? restCrimeData.crime_name,
  crime_points: crime.points ?? restCrimeData.crime_points,
  crime_time_relevance:
    crime.time_relevance ?? restCrimeData.crime_time_relevance,
  crime_case_type: crime.type ?? restCrimeData.crime_case_type,
  crime_id: crime.id ?? restCrimeData.crime_id,
  crime_category: crime.category ?? restCrimeData.crime_category,
  crime_verdict_date: formatDate(crime_verdict_date),
  crime_court: court.court_name ?? restCrimeData.crime_court,
  crime_court_name: court.court_name ?? restCrimeData.crime_court_name,
  court_email: court.court_email ?? restCrimeData.court_email,
  crime_court_id: court.id ?? restCrimeData.crime_court_id,
  court_id: court.id ?? restCrimeData.court_id,
});
